import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import {
  getActiveOrderHistory,
  resetActiveOrderHistory,
} from '@kfc-global/react-shared/redux/Actions/OrderHistoryAction';

import HEADER_TRACK_ORDER_CONSTANTS, { MAX_RETRY_INTERVAL } from '../Constants/HeaderTrackOrderConstants';
import { DISPOSITION_KEYS, LOCAL_STORAGE_KEYS } from 'common/constants/SharedConstants';
import {
  isSupportedTenantCode,
  redirectToActiveOrdersPage,
  redirectToOrderProcessingPage,
  redirectToPaymentFailurePage,
  redirectToPaymentSuccessPage,
  safeUpperCase,
} from 'common/utilities/utils';
import { updatePostOrderDetails } from 'components/paymentProcessPage/Container/paymentProcessingFn';
import LazyLoaderWrapper from 'atoms/LazyLoaderWrapper';

const HeaderTrackOrderComponent = props => {
  const dispatch = useDispatch();
  const useHistoryHook = { history: useHistory() };
  const {
    tenantReducer,
    orderHistoryReducer,
    profileReducer,
    appStateReducer: { userStatus: { loggedIn } = {} } = {},
  } = useSelector(data => data);
  const [isActiveOrderAvailable, setIsActiveOrderAvailable] = useState(false);
  const [title, setTitle] = useState('');
  const [dispatchGetActiveOrderHistoryTimer, setDispatchGetActiveOrderHistoryTimer] = useState();

  const clearDispatchGetActiveOrderHistoryTimer = () => {
    if (dispatchGetActiveOrderHistoryTimer) {
      clearTimeout(dispatchGetActiveOrderHistoryTimer);
      setDispatchGetActiveOrderHistoryTimer(undefined);
    }
  };

  useEffect(() => {
    clearDispatchGetActiveOrderHistoryTimer();
    dispatch(resetActiveOrderHistory());
    return () => clearDispatchGetActiveOrderHistoryTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchGetActiveOrderHistory = () => {
    if (loggedIn && profileReducer?.customerId && tenantReducer?.tenantId && props?.isHeaderTrackOrderEnabled()) {
      const postBody = {
        OrderStatus: '',
        OrderMode: '',
      };
      dispatch(
        getActiveOrderHistory(
          {
            customerId: profileReducer?.customerId,
            tenantId: tenantReducer?.tenantId,
          },
          postBody,
        ),
      );
    }
  };

  useEffect(() => {
    clearDispatchGetActiveOrderHistoryTimer();
    dispatchGetActiveOrderHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileReducer?.customerId, tenantReducer?.tenantId]);

  const singleOrderTracker = postActiveOrderDetails => {
    if (postActiveOrderDetails?.id) {
      const postOrderDetails = { ...postActiveOrderDetails, customer: profileReducer?.basic };
      const orderDetails = updatePostOrderDetails(tenantReducer?.tenantId, postOrderDetails);
      orderDetails.postOrderDetails = {};
      sessionStorage.setItem(LOCAL_STORAGE_KEYS.ORDER_TRACKING, JSON.stringify(orderDetails));
      if (isSupportedTenantCode()) {
        redirectToOrderProcessingPage(useHistoryHook);
      } else {
        redirectToPaymentSuccessPage(useHistoryHook);
      }
    } else {
      console.error(JSON.stringify(postActiveOrderDetails));
      redirectToPaymentFailurePage(useHistoryHook);
    }
  };

  const recursiveDispatchGetActiveOrderHistory = () => {
    !dispatchGetActiveOrderHistoryTimer &&
      setDispatchGetActiveOrderHistoryTimer(
        setTimeout(() => {
          clearDispatchGetActiveOrderHistoryTimer();
          dispatchGetActiveOrderHistory();
        }, MAX_RETRY_INTERVAL),
      );
  };

  useEffect(() => {
    if (
      orderHistoryReducer &&
      orderHistoryReducer?.activeOrderHistory &&
      !orderHistoryReducer?.activeOrderHistoryLoading
    ) {
      if (orderHistoryReducer?.activeOrderHistory?.length) {
        if (tenantReducer?.componentOptions?.web?.global?.showBlackStripWithOrderTracker === false) {
          props.setShowBlackStripeContent(false);
        }

        !isActiveOrderAvailable && setIsActiveOrderAvailable(true);

        setTitle(getTitle());
        recursiveDispatchGetActiveOrderHistory();
      } else {
        isActiveOrderAvailable && setIsActiveOrderAvailable(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryReducer?.activeOrderHistory]);

  const getTitle = () => {
    if (orderHistoryReducer?.activeOrderHistory?.length === 1) {
      const postActiveOrderDetail = orderHistoryReducer?.activeOrderHistory[0];
      if (postActiveOrderDetail?.requestedDateTime?.isForNow === false) {
        const returnTxt = `${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.YOUR_ORDER)} # ${
          postActiveOrderDetail?.posOrderId
        } ${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.HAS_SCHEDULED)} `;
        if (safeUpperCase(postActiveOrderDetail?.service) === DISPOSITION_KEYS.DELIVERY) {
          return `${returnTxt} ${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.DELIVERY)} ${translateWithI18Next(
            HEADER_TRACK_ORDER_CONSTANTS.AT,
          )}`;
        } else if (safeUpperCase(postActiveOrderDetail?.service) === DISPOSITION_KEYS.PICKUP) {
          return `${returnTxt} ${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.PICKUP)} ${translateWithI18Next(
            HEADER_TRACK_ORDER_CONSTANTS.AT,
          )}`;
        } else {
          return `${returnTxt}`;
        }
      } else if (safeUpperCase(postActiveOrderDetail?.service) === DISPOSITION_KEYS.DELIVERY) {
        return `${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.YOUR_ORDER)} # ${
          postActiveOrderDetail?.posOrderId
        } ${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.HAS_BEEN_CONFIRMED)}`;
      } else if (safeUpperCase(postActiveOrderDetail?.service) === DISPOSITION_KEYS.PICKUP) {
        return `${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.WE_ARE_WORKING_ON)} ${
          orderHistoryReducer?.activeOrderHistory?.length
        } ${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.ORDERS_NOW)}`;
      }
    } else {
      return `${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.WE_ARE_WORKING_ON)} ${
        orderHistoryReducer?.activeOrderHistory?.length
      } ${translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.ORDERS_NOW)}`;
    }
  };

  const onTrackOrderBtnClickHandler = useCallback(() => {
    if (orderHistoryReducer?.activeOrderHistory?.length > 1) {
      redirectToActiveOrdersPage(useHistoryHook);
    } else if (orderHistoryReducer?.activeOrderHistory?.length === 1) {
      singleOrderTracker(orderHistoryReducer?.activeOrderHistory[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryReducer]);

  const getHeaderTrackOrderItems = () => {
    return (
      <LazyLoaderWrapper
        {...props}
        testId='HeaderTrackOrderItems'
        importPath='APP_HEADER_ORDER_TRACKER_VIEW'
        buttonText={translateWithI18Next(HEADER_TRACK_ORDER_CONSTANTS.TRACK_ORDER)}
        trackOrderTitle={title}
        onTrackOrderBtnClickHandler={onTrackOrderBtnClickHandler}
      ></LazyLoaderWrapper>
    );
  };

  return isActiveOrderAvailable && getHeaderTrackOrderItems();
};

export default HeaderTrackOrderComponent;
